import React from 'react';
import { clsx } from 'clsx';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import lineIcon from '../assets/images/footer/line.svg';
import linkIcon from '../assets/images/footer/link.svg';

type FooterLink = { path: string; label: string };

const BASE_URL = 'https://kotomil.com';
const COPY_FEEDBACK_MS = 3000;

const shareRowClassName =
    'group flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 shadow-sm transition-all hover:bg-gray-50 md:h-auto md:w-fit md:justify-start md:gap-2 md:rounded-md md:border-0 md:bg-transparent md:px-2 md:py-1 md:text-gray-600 md:shadow-none md:hover:bg-gray-100 md:hover:text-gray-900';

function buildShareUrls(message: string, hashtags: string) {
    const encodedMessage = encodeURIComponent(message);
    const encodedUrl = encodeURIComponent(BASE_URL);
    const encodedHashtags = encodeURIComponent(hashtags);
    return {
        line: `https://line.me/R/msg/text/?${encodedMessage}%20${encodedUrl}`,
        twitter: `https://twitter.com/share?url=${encodedUrl}&text=${encodedMessage}&hashtags=${encodedHashtags}`,
    };
}

async function copyTextWithLegacyFallback(text: string): Promise<void> {
    try {
        await navigator.clipboard.writeText(text);
        return;
    } catch (err) {
        console.error('URLのコピーに失敗しました:', err);
    }
    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
}

function useCopyWithFeedback(text: string, feedbackMs = COPY_FEEDBACK_MS) {
    const [isCopied, setIsCopied] = React.useState(false);
    const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    const copy = React.useCallback(async () => {
        await copyTextWithLegacyFallback(text);
        setIsCopied(true);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => setIsCopied(false), feedbackMs);
    }, [text, feedbackMs]);

    React.useEffect(
        () => () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        },
        [],
    );

    return { isCopied, copy };
}

function IconX({ className, size = 16 }: { className?: string; size?: number }) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="currentColor"
            className={className}
            aria-hidden
        >
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
    );
}

function IconCheck({ className, size = 14 }: { className?: string; size?: number }) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
            aria-hidden
        >
            <polyline points="20 6 9 17 4 12" />
        </svg>
    );
}

function BlockHeading({ children }: { children: React.ReactNode }) {
    return (
        <h4 className="mb-4 text-sm font-bold tracking-wider text-gray-900 border-b border-gray-100 pb-2">
            {children}
        </h4>
    );
}

function SupportBlock({ links, title }: { links: FooterLink[]; title: string }) {
    return (
        <div className="w-full text-left">
            <BlockHeading>{title}</BlockHeading>
            <ul className="space-y-3">
                {links.map((link) => (
                    <li key={link.path}>
                        <Link
                            to={link.path}
                            className="text-sm font-medium text-gray-600 transition-colors hover:text-gray-900 hover:underline"
                        >
                            {link.label}
                        </Link>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function LegalBlock({ links, title }: { links: FooterLink[]; title: string }) {
    return (
        <div className="w-full text-left">
            <BlockHeading>{title}</BlockHeading>
            <ul className="space-y-3">
                {links.map((link) => (
                    <li key={link.path}>
                        <Link
                            to={link.path}
                            className="text-sm font-medium text-gray-600 transition-colors hover:text-gray-900 hover:underline"
                        >
                            {link.label}
                        </Link>
                    </li>
                ))}
            </ul>
        </div>
    );
}

type ShareBlockProps = {
    title: string;
    copyLabel: string;
    copiedLabel: string;
    isCopied: boolean;
    onCopy: () => void;
    twitterLabel: string;
    lineLabel: string;
    twitterShareUrl: string;
    lineShareUrl: string;
};

function ShareBlock({
    title,
    copyLabel,
    copiedLabel,
    isCopied,
    onCopy,
    twitterLabel,
    lineLabel,
    twitterShareUrl,
    lineShareUrl,
}: ShareBlockProps) {
    return (
        <div className="w-full text-left">
            <BlockHeading>{title}</BlockHeading>
            <ul className="flex flex-row flex-wrap items-center gap-4 md:flex-col md:items-start md:gap-0 md:space-y-3">
                <li>
                    <button
                        type="button"
                        onClick={onCopy}
                        className={shareRowClassName}
                        aria-label={isCopied ? copiedLabel : copyLabel}
                    >
                        {isCopied ? (
                            <IconCheck className="text-green-600" size={18} />
                        ) : (
                            <img
                                src={linkIcon}
                                width="18"
                                className="opacity-60 transition-opacity group-hover:opacity-100"
                                alt=""
                            />
                        )}
                        <span className={clsx('hidden md:inline', isCopied && 'text-green-600')}>
                            {isCopied ? copiedLabel : copyLabel}
                        </span>
                    </button>
                </li>
                <li>
                    <a
                        href={twitterShareUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={shareRowClassName}
                        aria-label={twitterLabel}
                    >
                        <IconX size={18} />
                        <span className="hidden md:inline">{twitterLabel}</span>
                    </a>
                </li>
                <li>
                    <a
                        href={lineShareUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={shareRowClassName}
                        aria-label={lineLabel}
                    >
                        <img src={lineIcon} width="18" alt="" />
                        <span className="hidden md:inline">{lineLabel}</span>
                    </a>
                </li>
            </ul>
        </div>
    );
}

/**
 * ログイン後フッターリンクを設定画面下部へ集約（サポート・法令・シェア）。
 */
const SettingsSupportLegalShareSection: React.FC = () => {
    const { t } = useTranslation('footer');

    const supportLinks = t('links.support', { returnObjects: true }) as FooterLink[];
    const legalLinks = t('links.legal', { returnObjects: true }) as FooterLink[];

    const { line: lineShareUrl, twitter: twitterShareUrl } = buildShareUrls(
        t('share.message'),
        t('share.hashtags'),
    );

    const { isCopied, copy } = useCopyWithFeedback(BASE_URL);

    return (
        <section className="flex flex-col gap-4 pb-2">
            <h3 className="text-lg font-semibold text-gray-800">{t('sections.onSettingsPage.title')}</h3>
            <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 shadow-sm">
                <div className="flex flex-col gap-10 md:flex-row md:justify-between md:gap-8 lg:gap-12">
                    <SupportBlock links={supportLinks} title={t('sections.support.title')} />
                    <LegalBlock links={legalLinks} title={t('sections.legal.title')} />
                    <ShareBlock
                        title={t('share.title')}
                        copyLabel={t('share.copyLabel')}
                        copiedLabel={t('share.copiedLabel')}
                        isCopied={isCopied}
                        onCopy={copy}
                        twitterLabel={t('share.twitterLabel')}
                        lineLabel={t('share.lineLabel')}
                        twitterShareUrl={twitterShareUrl}
                        lineShareUrl={lineShareUrl}
                    />
                </div>
            </div>
        </section>
    );
};

export default SettingsSupportLegalShareSection;
